import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Building2, Eye, EyeOff, Lock, MessageSquareText, Phone, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthShell } from "@packages/ui";
import { useAuthStore } from "../../stores/authStore";
import { RegisterRequest, SmsCodePurpose } from "@packages/global";
import { appConfig } from "../../config/app.config";
import ThemeToggle from "../../components/ThemeToggle";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import SmsCodeCaptchaDialog from "./SmsCodeCaptchaDialog";

const RegisterPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsCaptchaOpen, setSmsCaptchaOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [formData, setFormData] = useState<RegisterRequest>({
    phone: "",
    code: "",
    password: "",
    name: "",
    organizationName: "",
    acceptedAgreement: false,
  });
  const [errors, setErrors] = useState<{ name?: string; phone?: string; code?: string; password?: string; organizationName?: string; captcha?: string; agreement?: string }>({});
  const { getCaptcha, register, sendSmsCode } = useAuthStore();
  const navigate = useNavigate();
  const features = [
    {
      icon: <User className="h-6 w-6" strokeWidth={2.2} />,
      title: t("register.feature1Title"),
      description: t("register.feature1Description"),
    },
    {
      icon: <Lock className="h-6 w-6" strokeWidth={2.2} />,
      title: t("register.feature2Title"),
      description: t("register.feature2Description"),
    },
    {
      icon: <ArrowRight className="h-6 w-6" strokeWidth={2.2} />,
      title: t("register.feature3Title"),
      description: t("register.feature3Description"),
    },
  ];

  const validateForm = (): boolean => {
    const nextErrors: { name?: string; phone?: string; code?: string; password?: string; organizationName?: string; agreement?: string } = {};
    if (!formData.name) nextErrors.name = t("register.nameRequired");
    if (!formData.organizationName) nextErrors.organizationName = t("register.organizationRequired");
    if (!formData.phone) {
      nextErrors.phone = t("register.phoneRequired");
    } else if (!isValidPhone(formData.phone)) {
      nextErrors.phone = t("register.phoneInvalid");
    }
    if (!formData.code) nextErrors.code = t("register.codeRequired");
    if (!formData.password) {
      nextErrors.password = t("register.passwordRequired");
    } else if (formData.password.length < 6) {
      nextErrors.password = t("register.passwordTooShort");
    }
    if (!acceptedAgreement) nextErrors.agreement = "请先同意用户协议和隐私声明";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await register({ ...formData, acceptedAgreement });
      navigate("/dashboard");
    } catch (error: any) {
      setErrors({ phone: error.response?.data?.error || t("register.failed") });
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: Exclude<keyof RegisterRequest, "acceptedAgreement">) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    };

  const handleSendCode = async () => {
    if (!isValidPhone(formData.phone)) {
      setErrors({ phone: t("register.phoneInvalid") });
      return;
    }
    setSmsCaptchaOpen(true);
  };

  const handleConfirmSendCode = async ({ captchaId, captchaCode }: { captchaId: string; captchaCode: string }) => {
    setSendingCode(true);
    try {
      await sendSmsCode({
        phone: formData.phone.trim(),
        purpose: SmsCodePurpose.REGISTER,
        captchaId,
        captchaCode,
      });
      startCountdown(setCountdown);
    } finally {
      setSendingCode(false);
    }
  };

  return (
    <AuthShell
      appName={appConfig.name}
      appDescription="智能体工作台"
      title={t("register.title")}
      subtitle={t("register.subtitle")}
      features={features}
      homeAction={
        <button
          type="button"
          onClick={() => { navigate("/"); }}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-border-subtle bg-bg-surface/90 px-3 text-sm font-medium text-text-secondary shadow-sm backdrop-blur transition-colors hover:text-text-primary lg:border-white/60 lg:bg-white/10 lg:text-white lg:hover:bg-white/20 lg:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("register.backHome")}
        </button>
      }
      topBar={
        <>
          <div className="w-10">
            <ThemeToggle compact />
          </div>
          <div className="h-6 w-px bg-border-subtle" />
          <div className="w-10">
            <LanguageSwitcher compact />
          </div>
        </>
      }
      brandMark={
        <div className="grid grid-cols-2 gap-1.5">
          {[0, 1, 2, 3].map((cell) => (
            <div
              key={cell}
              className="h-[21px] w-[21px] rounded-[3px] border-[2.5px] border-white"
            />
          ))}
        </div>
      }
      brandActionLabel={t("register.backHome")}
      onBrandClick={() => { navigate("/"); }}
      supportMeta={t("register.hint")}
      footerAction={
        <button
          type="button"
          onClick={() => { navigate("/login"); }}
          className="font-medium"
        >
          {t("register.backToLogin")}
        </button>
      }
      form={() => (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-text-primary">
              {t("register.name")}
            </label>
            <div className="relative">
              <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange("name")}
                placeholder={t("register.namePlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.name ? "border-danger" : "border-border-default"
                }`}
              />
            </div>
            {errors.name ? <p className="text-sm text-danger">{errors.name}</p> : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="organizationName" className="block text-sm font-medium text-text-primary">
              {t("register.organization")}
            </label>
            <div className="relative">
              <Building2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="organizationName"
                name="organizationName"
                type="text"
                autoComplete="organization"
                value={formData.organizationName}
                onChange={handleChange("organizationName")}
                placeholder={t("register.organizationPlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.organizationName ? "border-danger" : "border-border-default"
                }`}
              />
            </div>
            {errors.organizationName ? <p className="text-sm text-danger">{errors.organizationName}</p> : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-text-primary">
              {t("register.phone")}
            </label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange("phone")}
                placeholder={t("register.phonePlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.phone ? "border-danger" : "border-border-default"
                }`}
              />
            </div>
            {errors.phone ? <p className="text-sm text-danger">{errors.phone}</p> : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="code" className="block text-sm font-medium text-text-primary">
              {t("register.code")}
            </label>
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <MessageSquareText className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={formData.code}
                  onChange={handleChange("code")}
                  placeholder={t("register.codePlaceholder")}
                  className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                    errors.code ? "border-danger" : "border-border-default"
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sendingCode || countdown > 0}
                className="h-11 w-[112px] shrink-0 rounded-lg border border-border-default bg-bg-form text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
              >
                {countdown > 0 ? `${countdown}s` : sendingCode ? t("register.sendingCode") : t("register.sendCode")}
              </button>
            </div>
            {errors.code ? <p className="text-sm text-danger">{errors.code}</p> : null}
          </div>

          <SmsCodeCaptchaDialog
            isOpen={smsCaptchaOpen}
            onClose={() => setSmsCaptchaOpen(false)}
            getCaptcha={getCaptcha}
            onConfirm={handleConfirmSendCode}
            title={t("register.smsCaptchaTitle")}
            description={t("register.smsCaptchaDescription")}
            label={t("register.captcha")}
            placeholder={t("register.captchaPlaceholder")}
            refreshLabel={t("register.refreshCaptcha")}
            submitLabel={t("register.sendCode")}
            submittingLabel={t("register.sendingCode")}
            cancelLabel={t("register.cancel")}
            requiredMessage={t("register.captchaRequired")}
            failedMessage={t("register.codeSendFailed")}
          />

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-text-primary">
              {t("register.password")}
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange("password")}
                placeholder={t("register.passwordPlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-11 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.password ? "border-danger" : "border-border-default"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:text-text-primary"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.password ? (
              <p className="text-sm text-danger">{errors.password}</p>
            ) : null}
          </div>

          <div>
            <label className="flex items-start gap-2 text-[12.5px] leading-5 text-text-secondary">
              <input
                type="checkbox"
                checked={acceptedAgreement}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setAcceptedAgreement(checked);
                  setFormData((current) => ({ ...current, acceptedAgreement: checked }));
                  setErrors((current) => ({ ...current, agreement: undefined }));
                }}
                className="mt-0.5 h-4 w-4 rounded border border-border-default bg-bg-form text-brand-600 focus:ring-brand-600"
              />
              <span>
                我已阅读并同意
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="mx-1 font-semibold text-primary-ink"
                >
                  用户协议与隐私声明
                </a>
              </span>
            </label>
            {errors.agreement ? <p className="mt-1 text-sm text-danger">{errors.agreement}</p> : null}
          </div>

          <button
            type="submit"
            disabled={loading || !acceptedAgreement}
            className="h-11 w-full rounded-lg bg-action-primary text-sm font-semibold text-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? t("register.registering") : t("register.submit")}
          </button>
        </form>
      )}
    />
  );
};

function isValidPhone(phone: string): boolean {
  return /^1[3-9]\d{9}$/.test(phone.trim());
}

function startCountdown(setCountdown: React.Dispatch<React.SetStateAction<number>>) {
  setCountdown(60);
  const timer = window.setInterval(() => {
    setCountdown((value) => {
      if (value <= 1) {
        window.clearInterval(timer);
        return 0;
      }
      return value - 1;
    });
  }, 1000);
}

export default RegisterPage;
